import {
    Badge,
    Box,
    Button,
    Flex,
    FormControl,
    FormLabel,
    Heading,
    HStack,
    Input,
    Select,
    SimpleGrid,
    Spinner,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorModeValue,
    useToast,
} from "@chakra-ui/react";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import CardHeader from "components/Card/CardHeader";
import {fetchSchoolListForDropdown} from "common-services";
import React, {useCallback, useEffect, useState} from "react";
import {FiChevronLeft, FiChevronRight, FiRefreshCw, FiSearch} from "react-icons/fi";
import {searchBellLogs} from "../../../Services/BellLogService";

const INITIAL_FILTERS = {schoolId: "", bellType: "", from: "", to: ""};

const getLogList = (data) =>
    data?.content || data?.bellLogs || data?.logs || data?.items || [];

const getTotalElements = (data, rows) =>
    data?.totalElements ?? data?.totalItems ?? data?.total ?? rows.length;

const getTotalPages = (data, totalElements, size) =>
    data?.totalPages ?? Math.max(1, Math.ceil(totalElements / size));

const getValue = (log, keys, fallback = "-") => {
    const key = keys.find((item) => log?.[item] !== undefined && log?.[item] !== null && log?.[item] !== "");
    return key ? log[key] : fallback;
};

const formatLogDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    const formattedDate = date.toLocaleDateString("en-US", {month: "long", day: "numeric", year: "numeric"});
    const formattedTime = date.toLocaleTimeString("en-US", {hour: "numeric", minute: "2-digit", hour12: true});
    return `${formattedDate} ${formattedTime}`;
};

const statusColor = (status) => {
    const value = String(status || "").toUpperCase();
    if (value.includes("FAIL") || value.includes("ERROR")) return "red";
    if (value.includes("PLAY")) return "green";
    return "gray";
};

function BellLog() {
    const toast = useToast();
    const textColor = useColorModeValue("gray.700", "white");
    const mutedColor = useColorModeValue("gray.500", "gray.400");
    const [schools, setSchools] = useState([]);
    const [filters, setFilters] = useState(INITIAL_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS);
    const [logs, setLogs] = useState([]);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(20);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const loadSchools = async () => {
            try {
                const response = await fetchSchoolListForDropdown();
                setSchools(response?.data || []);
            } catch (error) {
                toast({title: "Unable to load schools", status: "error", duration: 4000, isClosable: true});
            }
        };
        loadSchools();
    }, [toast]);

    const fetchLogs = useCallback(async (activeFilters, targetPage, targetSize) => {
        setLoading(true);
        try {
            const request = {
                schoolId: activeFilters.schoolId ? Number(activeFilters.schoolId) : null,
                bellType: activeFilters.bellType || null,
                from: activeFilters.from || null,
                to: activeFilters.to || null,
            };
            const response = await searchBellLogs(request, targetPage, targetSize);
            const rows = getLogList(response?.data);
            const count = getTotalElements(response?.data, rows);
            setLogs(rows);
            setTotalElements(count);
            setTotalPages(getTotalPages(response?.data, count, targetSize));
        } catch (error) {
            setLogs([]);
            setTotalElements(0);
            setTotalPages(1);
            toast({
                title: "Unable to fetch bell logs",
                description: error?.message || error?.errorMessage || "Please try again.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    }, [toast]);

    useEffect(() => {
        fetchLogs(appliedFilters, page, size);
    }, [appliedFilters, fetchLogs, page, size]);

    const handleFilterChange = (event) => {
        const {name, value} = event.target;
        setFilters((current) => ({...current, [name]: value}));
    };

    const handleSearch = (event) => {
        event.preventDefault();
        setPage(0);
        setAppliedFilters({...filters});
    };

    const handleReset = () => {
        setFilters(INITIAL_FILTERS);
        setPage(0);
        setAppliedFilters(INITIAL_FILTERS);
    };

    return (
        <Flex direction="column" pt={{base: "120px", md: "75px"}} gap="20px">
            <Flex justify="space-between" align={{base: "flex-start", md: "center"}} direction={{base: "column", md: "row"}} gap="12px">
                <Box>
                    <Heading as="h1" size="lg" color={textColor}>Bell Log</Heading>
                    <Text color={mutedColor} mt="4px">Every bell played across schools, newest first.</Text>
                </Box>
                <Button leftIcon={<FiRefreshCw/>} onClick={() => fetchLogs(appliedFilters, page, size)} isLoading={loading} variant="outline">
                    Refresh
                </Button>
            </Flex>

            <Card>
                <CardHeader pb="16px">
                    <Text fontWeight="semibold" color={textColor}>Search filters</Text>
                </CardHeader>
                <CardBody>
                    <Box as="form" onSubmit={handleSearch} width="100%">
                        <SimpleGrid columns={{base: 1, md: 2, xl: 4}} spacing="16px">
                            <FormControl>
                                <FormLabel fontSize="sm">School</FormLabel>
                                <Select name="schoolId" value={filters.schoolId} onChange={handleFilterChange} placeholder="All schools">
                                    {schools.map((school) => (
                                        <option key={school.value} value={school.value}>{school.label}</option>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="sm">Bell type</FormLabel>
                                <Select name="bellType" value={filters.bellType} onChange={handleFilterChange} placeholder="All types">
                                    <option value="BELL">Bell</option>
                                    <option value="MUSIC">Music</option>
                                    <option value="NATIONAL_ANTHEM">National Anthem</option>
                                </Select>
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="sm">From</FormLabel>
                                <Input name="from" type="date" value={filters.from} onChange={handleFilterChange}/>
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="sm">To</FormLabel>
                                <Input name="to" type="date" value={filters.to} onChange={handleFilterChange}/>
                            </FormControl>
                        </SimpleGrid>
                        <HStack mt="20px" spacing="12px">
                            <Button type="submit" colorScheme="teal" leftIcon={<FiSearch/>} isLoading={loading}>Search</Button>
                            <Button type="button" variant="ghost" onClick={handleReset}>Clear filters</Button>
                            <FormControl width="auto" ml="auto">
                                <Select size="sm" value={size} onChange={(event) => { setPage(0); setSize(Number(event.target.value)); }}>
                                    {[10, 20, 50, 100].map((value) => <option key={value} value={value}>{value} / page</option>)}
                                </Select>
                            </FormControl>
                        </HStack>
                    </Box>
                </CardBody>
            </Card>

            <Card overflowX="auto">
                <CardHeader pb="16px">
                    <Flex justify="space-between" align="center" width="100%">
                        <Text fontWeight="semibold" color={textColor}>Bell events</Text>
                        <Badge colorScheme="teal" px="10px" py="4px" borderRadius="full">{totalElements} records</Badge>
                    </Flex>
                </CardHeader>
                <CardBody>
                    <Box width="100%" overflowX="auto">
                        <Table variant="simple" minWidth="900px">
                            <Thead>
                                <Tr>
                                    <Th>Played at</Th>
                                    <Th>School</Th>
                                    <Th>Bell</Th>
                                    <Th>Type</Th>
                                    <Th>Scheduled time</Th>
                                    <Th>Status</Th>
                                    <Th>Source</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {!loading && logs.map((log, index) => (
                                    <Tr key={getValue(log, ["id", "bellLogId"], `${page}-${index}`)}>
                                        <Td whiteSpace="nowrap">{formatLogDate(getValue(log, ["playedAt", "createdAt", "date", "timestamp"], ""))}</Td>
                                        <Td fontWeight="medium">{getValue(log, ["schoolName", "school"])}</Td>
                                        <Td>{getValue(log, ["bellName", "name", "bell"])}</Td>
                                        <Td><Badge colorScheme="blue">{getValue(log, ["bellType", "type"])}</Badge></Td>
                                        <Td whiteSpace="nowrap">{getValue(log, ["hourTime", "scheduledTime"])}</Td>
                                        <Td><Badge colorScheme={statusColor(getValue(log, ["status"], ""))}>{getValue(log, ["status"])}</Badge></Td>
                                        <Td>{getValue(log, ["source"])}</Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                        {loading && <Flex justify="center" py="50px"><Spinner color="teal.400" size="lg"/></Flex>}
                        {!loading && logs.length === 0 && (
                            <Flex direction="column" align="center" py="50px">
                                <Heading size="sm" color={textColor}>No bell logs found</Heading>
                                <Text color={mutedColor} mt="6px">Try changing or clearing the filters.</Text>
                            </Flex>
                        )}
                    </Box>
                </CardBody>
                <Flex justify="space-between" align="center" px="22px" pb="22px" gap="12px" wrap="wrap">
                    <Text color={mutedColor} fontSize="sm">Page {page + 1} of {totalPages}</Text>
                    <HStack>
                        <Button leftIcon={<FiChevronLeft/>} onClick={() => setPage((current) => Math.max(0, current - 1))} isDisabled={loading || page === 0} size="sm">Previous</Button>
                        <Button rightIcon={<FiChevronRight/>} onClick={() => setPage((current) => Math.min(totalPages - 1, current + 1))} isDisabled={loading || page + 1 >= totalPages} size="sm">Next</Button>
                    </HStack>
                </Flex>
            </Card>
        </Flex>
    );
}

export default BellLog;
